import { V1Container } from '@kubernetes/client-node'
import Ajv, { ErrorObject, Schema } from 'ajv'
import archiver = require('archiver')
import axios from 'axios'
import * as crypto from 'crypto'
import * as fs from 'fs'
import * as http from 'http'
import * as https from 'https'
import { JSONPath } from "jsonpath-plus"
import * as _ from 'lodash'
import minimatch = require('minimatch')
import * as StreamZip from 'node-stream-zip'
import * as path from 'path'
import type { Logger } from 'pino'
import pino = require('pino')
import { Writable } from 'stream'
import { promisify } from 'util'
import { v4 as uuidv4 } from 'uuid'
import { KV, KVString } from 'yam-types'
import { APP_MODEL_FILES, JSON_PATH_PREFIX } from '../constants'
import { EnvPatternValues } from '../main'
const ajv = new Ajv()

export const logStream = new Writable()

const additionalStream: Writable[] = []
logStream._write = (chunk, encoding, callback) => {
  additionalStream.forEach(w => w._write(chunk, encoding, () => { /**/ }))
  process.stdout._write(chunk, encoding, callback)
}

/**
 * Create logger for different sub-module
 * 
 * @param name logger name
 * @returns Pino logger
 */
export const createLogger = (name: string): Logger => {
  return pino({
    name,
    base: {},
    level: (process.env.TRACE === "true") ? 'trace' : (process.env.DEBUG === "true") ? 'debug' : 'info',
    prettyPrint: {
      colorize: !process.env.VSCODE_PID,
      translateTime: 'yyyy-mm-dd HH:MM:ss.l',
      ignore: 'pid,hostname'
    }
  }, logStream)
}

const log = createLogger('util')

export const addLogStream = (stream: Writable): void => {
  additionalStream.push(stream)
}

export const switchToFileLogMode = async (filePrefix: string, dir?: string, cleanOldFilesBefore?: number): Promise<string> => {
  const cleanTimestamp = (cleanOldFilesBefore || Date.now()) - 30 * 86400 * 1000
  // clean old log files
  const baseDir = dir || process.cwd()
  const files = await fsReadDir(baseDir)
  files.forEach(async file => {
    if (file.startsWith(filePrefix)) {
      const logFile = path.join(dir || '.', file)
      const stat = fs.statSync(logFile)
      if (cleanTimestamp > stat.mtimeMs) {
        await fsRemove(logFile)
      }
    }
  })

  // change to file log stream
  const now = new Date()
  const logFile = path.join(baseDir, filePrefix + `.${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}.log`)
  logStream._write = (chunk, encoding, callback) => {
    fs.appendFile(logFile, chunk, { encoding }, callback)
  }
  return logFile
}

/**
 * Split by multiple delimiter, including: ',' '/' ';' and whitespace
 * 
 * @param str input string
 * @returns split string array
 */
export const splitAndTrim = (str: string): string[] => {
  return _.split(str, /[/;,\s]+/).filter(s => !!s)
}

export const splitAndTrimComma = (str?: string): string[] => {
  if (!str) {
    return []
  }
  return _.split(str, /[,\s]+/)
}

// eslint-disable-next-line @typescript-eslint/ban-types
export const jsonPath = (path: string, obj: object): unknown[] => {
  if (!path) {
    return []
  }
  return JSONPath({ path: normalizeJsonPath(path), json: obj }) as unknown as unknown[] || []
}

export const normalizeJsonPath = (expr: string, fetchParent?: boolean): string => {
  if (fetchParent) {
    // jsonpath-plus grammar, to fetch parent object
    expr += "^"
  }
  if (!expr.startsWith(JSON_PATH_PREFIX)) {
    expr = JSON_PATH_PREFIX + expr
  }
  return expr
}

export const mergeObject = (target: unknown, source: unknown, replaceArray?: boolean, skipExisting?: boolean): unknown => {
  return _.mergeWith(target, source, ((objValue, srcValue) => {
    if (skipExisting) {
      if (objValue === undefined) {
        return srcValue
      } else {
        return objValue
      }
    }
    if (_.isArray(objValue)) {
      if (replaceArray) {
        // replace whole array
        return srcValue
      } else {
        // append array items by default
        return objValue.concat(srcValue)
      }
    }
    return
  }))
}

export const validateJsonSchema = (current: unknown, jsonSchema: unknown): ErrorObject[] | null | undefined => {
  const validate = ajv.compile(jsonSchema as Schema)
  if (validate(current)) {
    return
  } else {
    return validate.errors
  }
}

export const wait = (interval: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, interval)
  })
}

export function fileExists(filepath: string): boolean {
  try {
    fs.accessSync(filepath)
    return true
  } catch (ignore) {
    return false
  }
}

/**
 * Checking if 'app.yaml, yam.yaml' like files exists
 */
export function checkApplicationModelPath(workDir: string, cache?: Map<string, string>): string {
  for (const name of APP_MODEL_FILES) {
    if (cache?.has(name) || fs.existsSync(path.join(workDir, name))) {
      return name
    }
  }
  throw new Error(`no 'app.yaml' or 'yam.yaml' file found in directory: '${workDir}'`)
}

/**
 * examples: 1s -> second, 2m -> minutes, 3h -> hours, 4d -> days, 5w -> weeks, 6y -> years
 * 
 * @param str date/interval string
 */
export function fromHumanInterval(str: string): number {
  if (!str.match(/^\d+(s|m|h|d|w|y)$/)) {
    throw new Error('invalid time interval: ' + str)
  }
  const char = str[str.length - 1]
  return parseInt(str.substring(0, str.length - 1)) * (
    char === "s" ? 1000 : char === "m" ? 60000 : char === "h" ? 3600000 :
      char === "d" ? 86400000 : char === "w" ? (86400000 * 7) :
        char === "y" ? (86400000 * 365) : 1000)
}

export function toHumanInterval(time: number, base?: string): string {
  if (!base) {
    base = ''
  }
  if (time >= 86400000) {
    const days = Math.floor(time / 86400000)
    return toHumanInterval(time - (days * 86400000), base + days + 'd')
  } else if (time >= 3600000) {
    const hours = Math.floor(time / 3600000)
    return toHumanInterval(time - (hours * 3600000), base + hours + 'h')
  } else if (time >= 60000) {
    const minutes = Math.floor(time / 60000)
    return toHumanInterval(time - (minutes * 60000), base + minutes + 'm')
  } else if (time <= 0) {
    return base
  } else {
    return base + Math.floor((time / 1000)) + 's'
  }
}

export function timeOffSet(cmpDate?: Date, baseDate?: Date): string {
  if (!cmpDate) {
    return 'N/A'
  }
  if (!baseDate) {
    baseDate = new Date()
  }
  const offset = cmpDate.getTime() - (baseDate.getTime())
  if (offset > 0) {
    return toHumanInterval(offset) + ' Later'
  } else {
    return toHumanInterval(-offset) + ' Ago'
  }
}

export const fsExist = (path: fs.PathLike): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    fs.stat(path, (err) => {
      if (err) {
        log.debug(`[warn] fsExist returns error: ${err.message}`)
        resolve(false)
      } else {
        resolve(true)
      }
    })
  })
}
export const fsRead = promisify(fs.readFile)
export const fsReadDir = promisify(fs.readdir)
export const fsWrite = promisify(fs.writeFile)
export const fsAppend = promisify(fs.appendFile)
export const fsMkdir = promisify(fs.mkdir)
export const fsRmDirEmpty = promisify(fs.rmdir)

export const fsRemove = promisify(fs.unlink)

export async function fsRmdir(dir: string): Promise<void> {
  if (!dir) {
    return
  }
  const entries = await fsReadDir(dir, { withFileTypes: true })
  const results = await Promise.all(entries.map(entry => {
    const fullPath = path.join(dir, entry.name)
    const task = entry.isDirectory() ? fsRmdir(fullPath) : fsRemove(fullPath)
    return task.catch(error => ({ error }))
  }))
  results.forEach(result => {
    // Ignore missing files/directories; bail on other errors
    if (result && result.error.code !== 'ENOENT') throw result.error
  })
  await fsRmDirEmpty(dir)
}

export function zipDirectory(source: string, out: string): Promise<void> {
  const archive = archiver('zip', { zlib: { level: 9 } })
  const stream = fs.createWriteStream(out)

  return new Promise((resolve, reject) => {
    archive
      .directory(source, false)
      .on('error', (err: Error) => reject(err))
      .pipe(stream)

    stream.on('close', () => resolve())
    archive.finalize()
  })
}

export async function unzip(source: string, destDir?: string): Promise<string> {
  if (!destDir) {
    destDir = uuidv4()
  }
  if (await fsExist(source)) {
    await fsMkdir(destDir)
    const zip = new StreamZip.async({ file: source })
    await zip.extract('', destDir)
    await zip.close()
    return destDir
  }
  throw new Error(`File not found, expect: ${source}`)
}

export function downloadUrl(url: string, dest: string, token?: string, timeoutMills?: number): Promise<string> {
  if (!url || !dest || dest.indexOf('/') !== -1) {
    throw new Error(`Invalid download param: ${url} => ${dest}`)
  }
  const file = fs.createWriteStream(dest)
  return new Promise((resolve, reject) => {
    const request = (url.startsWith('https') ? https : http).get(url, (response) => {
      response.pipe(file)
      file.on('finish', () => {
        file.close()
        resolve(dest)
      })
    }).on('error', (err) => {
      if (fs.existsSync(dest)) {
        fs.unlinkSync(dest)
      }
      reject(err)
    }).on("timeout", () => {
      if (fs.existsSync(dest)) {
        fs.unlinkSync(dest)
      }
      reject(new Error('Request Timeout'))
    })
    request.setTimeout(timeoutMills || 1200000)
    if (token) {
      request.setHeader("Authentication", "Bearer " + token)
    }
  })
}

export function generateWorkloadNameWithEnv(app: string, envName: string): string {
  const envTmp = trimAndLowerAndRemoveSlash(envName)
  return `${app}-${envTmp.replace(/\//g, '-')}`
}

export function generateEnvSuffix(envName: string): string {
  const envTmp = trimAndLowerAndRemoveSlash(envName)
  return envTmp.replace(/\//g, '-')
}

export function trimAndLowerAndRemoveSlash(str: string): string {
  if (!str) {
    return str
  }
  let tmp = str.trim().toLowerCase()
  if (tmp.startsWith('/')) {
    tmp = tmp.substring(1)
  }
  if (tmp.endsWith('/')) {
    tmp = tmp.substring(0, tmp.length - 1)
  }
  return tmp
}

export const sleep = wait

export const uuid = uuidv4

export function sha256(input: string): string {
  if (!input) {
    return 'empty'
  }
  return crypto.createHash('sha256').update(input).digest('hex')
}

export const randomString = (length: number, chars: string): string => {
  const charsLength = chars.length
  const randomBytes = crypto.randomBytes(length)
  const result = new Array(length)
  let cursor = 0
  for (let i = 0; i < length; i++) {
    cursor += randomBytes[i]
    result[i] = chars[cursor % charsLength]
  }
  return result.join('')
}

export const randomAlphabet = (length: number): string => {
  return randomString(length, "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ")
}

export const randomNumeric = (length: number): string => {
  return randomString(length, "0123456789")
}

export const randomAlphabetNumeric = (length: number): string => {
  return randomString(length, "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789")
}

export function searchMatchedVariableByEnv<T>(envKeyValue: { [key: string]: T } | undefined, envLocators: string[]): T | undefined {
  if (!envKeyValue || typeof envKeyValue !== "object") {
    return undefined
  }
  for (let i = envLocators.length; i > 0; i--) {
    const searchEnv = envLocators.slice(0, i).join('/')
    const result = envKeyValue[searchEnv]
    if (result !== undefined) {
      return result
    }
  }
  return undefined
}

export function metadataMatch(metadata: KV, conditionMetadata?: KVString): boolean {
  if (!metadata) {
    return false
  }
  if (!conditionMetadata) {
    return true
  }
  let matches = true
  for (const metaKey in conditionMetadata) {
    if (conditionMetadata[metaKey] !== metadata[metaKey]) {
      matches = false
      continue
    }
  }
  return matches
}

export function injectParamToCommandArgsOrEnv(
  containers?: V1Container[], injectParams?: string[], appendMode?: boolean, targetEnvVars?: string[], targetCommandBinary?: string
): boolean {
  if (!injectParams || !containers || containers.length === 0) {
    return false
  }
  const injectParamStr = ' ' + injectParams.join(' ') + ' '
  let injected = false
  if (containers && containers.length > 0) {
    for (const container of containers) {
      if (container.env && container.env.length > 0 && (targetEnvVars && targetEnvVars.length > 0)) {
        const envMatched = container.env.find(e => targetEnvVars.includes(e.name))
        if (envMatched) {
          injected = true
          if (appendMode) {
            envMatched.value = (envMatched.value || '') + injectParamStr
          } else {
            envMatched.value = injectParamStr + (envMatched.value || '')
          }
        }
      }

      if (!targetCommandBinary && container.args) {
        injected = true
        if (appendMode) {
          container.args.push(...injectParams)
        } else {
          container.args.unshift(...injectParams)
        }
      }

      if (container.command && targetCommandBinary) {
        // in case of command is string, which is fine for the engine 
        if (typeof container.command === "string" && (container.command as string).startsWith(targetCommandBinary)) {
          injected = true
          // startsWith the cmd, split space and inject into it
          const tmpCmd = (container.command as string).split(' ')
          if (appendMode) {
            container.command = container.command + injectParamStr as unknown as string[]
          } else {
            container.command = (tmpCmd[0] + injectParamStr + tmpCmd.slice(1).join(' ')) as unknown as string[]
          }
        } else if (container.command.length > 1 && container.command[0] === targetCommandBinary) {
          injected = true
          if (appendMode) {
            container.command.push(...injectParams)
          } else {
            container.command = [container.command[0], ...injectParams, ...container.command.slice(1)]
          }
        }
      }
    }
  }
  return injected
}

export function parseBaseType(str?: string | boolean | number): string | boolean | number | undefined {
  if (str === undefined) {
    return
  }
  // avoid empty/null parameter
  if (typeof str !== "string") {
    return str
  }
  if (!str.match(/^\d{1,32}\.?\d{0,32}$/)) {
    if (str === "true") {
      return true
    } else if (str === "false") {
      return false
    } else {
      if (str.startsWith('"') || str.startsWith("'")) {
        str = str.substring(1)
      }
      if (str.endsWith('"') || str.endsWith("'")) {
        str = str.substring(0, str.length - 1)
      }
      return str
    }
  } else {
    return parseFloat(str)
  }
}

/**
 * Get all values matching envName from wildCardEnvValues and merge them together
 * @param envName specific environment name
 * @param wildCardEnvValues key is a wildcard environment name
 * @returns 
 */
export function reduceWildcardEnvValue
  <V extends KV | unknown[]>
  (
    envName: string,
    wildCardEnvValues: EnvPatternValues<V>[]
  ): V | undefined {

  const values = wildCardEnvValues
    .filter(v => matchWildcardEnv(v.envPattern, envName))
    .map(v => v.value)

  if (values.length === 0) {
    return undefined
  }

  if (Array.isArray(values[0])) {
    return values.flat() as V
  }

  return values.reduce((prev, cur) => ({ ...prev, ...cur }), {} as V)
}


// Lookup the value matching envName from the object that key is a wildcard environment name
export function lookupWildcardKeyObj(envName: string, wildcardKeyObj: { [k: string]: unknown } = {}): unknown {
  const key = Object.keys(wildcardKeyObj).reverse().find(k => matchWildcardEnv(k, envName))
  if (!key) {
    return null
  }
  return wildcardKeyObj[key]
}

export function matchWildcardEnv(wildcardEnv: string, cmpEnv: string): boolean {
  const res = minimatch(cmpEnv, wildcardEnv)
  log.debug(`wildcard env matching: ${wildcardEnv} ${res ? 'matches' : 'NOT matches'} ${cmpEnv}`)
  return res
}

export function isMultiEnvClusterMode(clusterName: string, multiEnvModeClusters?: string[]): boolean {
  let multiEnvMode = false
  const multiEnvClusters = multiEnvModeClusters
  if (multiEnvClusters) {
    for (const wildcard of multiEnvClusters) {
      if (matchWildcardEnv(wildcard, clusterName)) {
        multiEnvMode = true
      }
    }
  }
  return multiEnvMode
}

export const axiosClient = axios